import { AppState } from '@/store/types';
import { type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import { VueHeadClient } from '@unhead/vue/client';
import { type SSRContext } from 'vue/server-renderer';
import { Store } from 'vuex';

export enum HttpStatusCode {
  OK = 200,
  Created = 201,
  Accepted = 202,
  NoContent = 204,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  InternalServerError = 500,
}

export enum PlatformName {
  Homegate = 'hg',
  Immoscout24 = 'is24',
}

export interface AppSSRContext extends SSRContext {
  url: string;
  smgSplitSession: SmgSplitSession;
}

export interface AppSSRInitInstance {
  appContent: string;
  store: Store<AppState>;
  head: VueHeadClient;
  preloadLinks: string;
  redirect?: string;
}

export enum NewValuationFlow {
  PropertyType,
  PropertySubType,
  UsageType,
  YearBuild,
  PropertyCondition,
  ConstructionStandard,
  Rooms,
  LivingSpace,
  LotSize,
  Location,
  SummaryPage,
}

export enum Pages {
  LP = 'landing',
  LLP = 'localLanding',
  Funnel = 'valuationFunnel',
  Result = 'result',
  Confirmation = 'confirmation',
  PDP = 'propertyDetails',
  PropertyOverview = 'propertyOverview',
}

// matches domus breakpoints
export const BREAKPOINTS = {
  domus_xs: 320,
  domus_sm: 375,
  domus_md: 768,
  domus_lg: 1024,
  domus_xl: 1200,

  funnel_container_max_width: 480, // matches funnel-container-max-width from variables.scss
};

export const BUILD_YEAR_THRESHOLD = 2000;
export const MIN_BUILD_YEAR = 1900;
export const MAX_BUILD_YEAR_FUTURE = 2;
export const MAX_BUILD_YEAR_CURRENT = new Date().getFullYear();
export const MAX_BUILD_YEAR = MAX_BUILD_YEAR_CURRENT + MAX_BUILD_YEAR_FUTURE;
export const MAX_LIVING_SPACE = 300;
export const MIN_LIVING_SPACE = 50;
export const MIN_LAND_AREA = 100;
export const MAX_LAND_AREA = 2000;
export const AXIOS_HEADERS = {
  E2E_TEST_HEADER: 'X-E2e-Test',
  API_KEY: 'X-Api-Key',
  REQUEST_SOURCE: 'X-Request-Source',
};
export const MAX_RENOVATION_YEAR = new Date().getFullYear();
export const MAX_TICK_AMOUNT = 5;
export const TEXT_PLACEHOLDER = '–';
export const STATIC_ASSETS_PRELOAD_PREFIX = 'preload-';

/* TODO: Introduce a new SplitIo flagset for UBS Immocheck funnel, when required.
 *  This one is for the property-valuation-vue project.
 */
export const FLAGSET_PROPERTY_VALUATION = 'valuation';

export enum ApartmentType {
  GARDEN_FLAT = 'garden_flat',
  FLOOR_FLAT = 'floor_flat',
  ATTIC = 'attic',
}

export enum HouseType {
  DETACHED_HOUSE = 'detached_house',
  SEMI_DETACHED_HOUSE = 'semidetached_house',
  ROW_CORNER_HOUSE = 'row_corner_house',
  ROW_HOUSE = 'row_house',
}

export enum PropertyTypeNumber {
  Apartment,
  House,
}

// Use for Immocheck 2.0 store
export enum PropertyTypeAndUsage {
  SINGLE_FAMILY_HOUSE = 'single_family_house',
  CONDOMINIUM = 'condominium',
  VACATION_HOUSE = 'vacation_house',
  VACATION_CONDOMINIUM = 'vacation_condominium',
}

export enum ClassOfProperty {
  MAIN = 'main_residence',
  HOLIDAY_HOME = 'second_residence',
}
export type MinergieStandardType = 'minergie' | 'none';

export enum ConstructionQualityType {
  BAD = 'bad',
  BAD_MEDIUM = 'bad-medium',
  MEDIUM = 'medium',
  MEDIUM_GOOD = 'medium-good',
  GOOD = 'good',
}

export enum StateBuildingType {
  VERY_GOOD = 'verygood',
  GOOD = 'good',
  MEDIUM = 'medium',
  BAD = 'bad',
}

export interface RadioItem {
  value: string;
  label: string;
  trackingValue?: string;
}

export interface ObjectPrice {
  currency: string;
  content: string;
}

export interface ImmocheckValuationRequestPayload {
  propertyObjectId: string;
  propertyType: PropertyTypeAndUsage | null;
  stateBuildingType: StateBuildingType | null;
  houseType: HouseType | null;
  apartmentType: ApartmentType | null;
  numberOfSeparateGarages: number | null;
  numberOfExternalParking: number | null;
  minergieStandardType: MinergieStandardType;
  constructionQualityType: ConstructionQualityType | null;
  rooms: number | null;
  floor: number | null;
  livingSpace: number | null;
  volumeIncludingGarageInhouse: number | null;
  landArea: number | null;
  objectPrice?: ObjectPrice;
  address: {
    streetName?: string;
    postCode?: string;
    townName: string;
    country: string;
  };
  buildYear: number | null;
}

export enum CampaignTpp {
  CoopHomegateGenericPriceCheckMortgage = 'CoopHomegate-UBS-generic-PriceCheck-Mortgage',
  CoopImmoScout24GenericPriceCheckMortgage = 'CoopImmoScout24-UBS-generic-PriceCheck-Mortgage',
}
